import type { FeatureFlag, WithFeatureFlags } from '@utils/featureFlags';
import type { ReactNode } from 'react';
import { createContext, useContext, useMemo } from 'react';
import { useUserContext } from './UserContext';

interface FeatureFlagProviderProps extends WithFeatureFlags {
	children: ReactNode;
}

export const FeatureFlagContext = createContext<WithFeatureFlags | null>(null);

export function useFeatureFlagContext() {
	const context = useContext(FeatureFlagContext);
	if (context == null) throw new Error('Feature Flag Context is not ready.');
	return context;
}

export const FeatureFlagProvider = ({ children, featureFlags: featureFlagsFromProps = [] }: FeatureFlagProviderProps) => {
	const { featureFlags: featureFlagsFromBusiness } = useUserContext();
	// Memoized so this provider does not hand its consumers a new value on every render of the tree
	// above it — it sits directly under `UserProvider`, so an unmemoized literal here would undo the
	// memoization there.
	const value = useMemo(() => {
		const allFeatureFlags = new Set<FeatureFlag>([...featureFlagsFromBusiness, ...featureFlagsFromProps]);
		return { featureFlags: [...allFeatureFlags] };
	}, [featureFlagsFromBusiness, featureFlagsFromProps]);

	return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>;
};
